import React, { useState } from 'react';
import { useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { StoryService } from '../../services/storyService';
import { Timestamp } from 'firebase/firestore';
import { canSaveStory } from '../../utils/authChecks';
import SignupPrompt from '../Account/SignupPrompt';
 
// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  primary:           '#6a37d4',
  primaryLight:      '#ae8dff',
  primaryContainer:  '#ede8ff',
  secondary:         '#006947',
  secondaryContainer:'#d1fae5',
  tertiary:          '#6f5900',
  tertiaryContainer: '#fed01b',
  surface:           '#f3f6ff',
  surfaceLow:        '#eaf1ff',
  surfaceHigh:       '#d6e3f9',
  surfaceLowest:     '#ffffff',
  onBackground:      '#252f3c',
  onSurface:         '#252f3c',
  onSurfaceVariant:  '#525c6a',
  outlineVariant:    '#a4aebe',
  error:             '#b41340',
  errorContainer:    '#ffd9df',
  surfaceSoft:       '#f6f7fb',
};
 
// Per-section accents
const ACCENTS = [
  { accent: C.primary,   light: C.primaryContainer,   iconBg: '#f0eaff' },
  { accent: C.secondary, light: C.secondaryContainer,  iconBg: '#e6fff4' },
  { accent: C.tertiary,  light: C.tertiaryContainer,   iconBg: '#fff8db' },
  { accent: C.error,     light: C.errorContainer,      iconBg: '#fff0f2' },
  { accent: C.primary,   light: C.primaryContainer,    iconBg: '#f0eaff' },
];
 
// Genre sticker colours
const GENRE_STICKERS = [
  { label: 'Fantasy',            bg: C.tertiaryContainer,  color: C.tertiary   },
  { label: 'Mystery',            bg: C.secondaryContainer, color: C.secondary  },
  { label: 'Sci-Fi',             bg: C.primaryContainer,   color: C.primary    },
  { label: 'Adventure',          bg: '#ffe4e4',            color: C.error      },
  { label: 'Comedy',             bg: '#fef9c3',            color: '#854d0e'    },
  { label: 'Scary',              bg: '#f3e8ff',            color: '#6b21a8'    },
  { label: 'Historical Fiction', bg: '#fef3c7',            color: '#92400e'    },
  { label: 'Realistic',          bg: C.tertiaryContainer,  color: C.tertiary   },
];
 
// ── Genre-specific idea sparks (Option A) ─────────────────────────────────────
// Structure: genreIdeaSparks[genre][sectionId] = string[]
const genreIdeaSparks: { [genre: string]: { [key: number]: string[] } } = {
  Fantasy: {
    1: ["A young wizard discovers their magic is different from everyone else's", "A hidden portal opens in an ordinary wardrobe", "A dragon egg is found beneath the hero's bed", "The village elder reveals a secret prophecy about your hero"],
    2: ["An ancient spell book falls from the sky", "A mysterious fairy godmother appears with a warning", "The enchanted forest starts dying and only your hero can save it", "A talking animal delivers an urgent message"],
    3: ["The hero's magic keeps misfiring at the worst moments", "A dark sorcerer captures their best friend", "The map to the magic realm turns out to be fake", "An enchanted curse begins spreading through the land"],
    4: ["The hero must break an ancient curse before midnight", "They face the dark wizard in a battle of spells", "They must choose between saving magic or saving their friend", "The final riddle of the dragon guardian must be solved"],
    5: ["Magic is restored and flowers bloom across the kingdom", "The hero finds their true magical gift", "Peace returns between humans and mythical creatures", "A new age of wonder begins for the whole land"],
  },
  Mystery: {
    1: ["A strange package arrives at the door with no return address", "The town's prized trophy has gone missing overnight", "Your hero notices something odd that nobody else sees", "A new kid at school is hiding a big secret"],
    2: ["A cryptic note is slipped under the hero's door", "Someone is framed for something they didn't do", "A witness vanishes before they can tell the truth", "The hero finds a clue that doesn't add up"],
    3: ["Every suspect has a perfect alibi — but one is lying", "The hero is warned to stop investigating", "A crucial piece of evidence goes missing", "A trusted friend starts acting suspiciously"],
    4: ["The real culprit is someone everyone trusted", "The hero must prove their theory with only minutes to spare", "Two suspects both had motive — only one is guilty", "The final clue was hidden in plain sight all along"],
    5: ["The truth comes out and everyone is stunned", "The hero is celebrated as the town's greatest detective", "An unexpected friendship forms with someone once suspected", "A new mystery is already waiting around the corner"],
  },
  'Sci-Fi': {
    1: ["Your hero lives on a space station orbiting a distant planet", "A message arrives from an unknown civilisation", "An experiment in the school lab goes spectacularly wrong", "Your hero discovers they have a special connection to technology"],
    2: ["A distress signal is detected from deep space", "The AI best friend starts behaving strangely", "A time-travel device is found in the school science room", "Robots across the city suddenly go offline"],
    3: ["The ship's oxygen supply is running dangerously low", "Two alien factions both want the hero to take their side", "A computer virus threatens to shut down the whole colony", "The hero's invention has an unexpected and dangerous flaw"],
    4: ["The hero must reprogram the rogue AI before it's too late", "They pilot the ship through an asteroid field to reach help", "A choice must be made: save the crew or save the planet", "The final code sequence must be entered in thirty seconds"],
    5: ["First contact with the alien species brings peace", "The hero's invention changes life across the galaxy forever", "Humans and robots learn to work together as equals", "The crew returns home as legends of the stars"],
  },
  Adventure: {
    1: ["Your hero finds an old map stuffed inside a library book", "A summer camping trip leads deep into uncharted jungle", "The hero's grandparent reveals a family secret about hidden treasure", "A storm washes the hero ashore on an unknown island"],
    2: ["A rival team of explorers is also searching for the prize", "The map leads to a forbidden zone nobody has entered in decades", "An injured stranger begs the hero for help finding something", "The first clue sends the hero far from home"],
    3: ["A river crossing nearly ends the whole expedition", "The rival team sabotages the hero's supplies", "Someone in the group wants to turn back — or worse", "The path forward is blocked by a dangerous wild animal"],
    4: ["The treasure vault has one final deadly trap", "The hero and the rival must work together to survive", "A sacrifice must be made to reach the ultimate goal", "The last obstacle is the most physically demanding yet"],
    5: ["The treasure turns out to be more meaningful than gold", "The hero earns the respect of everyone who doubted them", "A bond forged on the adventure lasts a lifetime", "The journey home begins with a great story to tell"],
  },
  Comedy: {
    1: ["Your hero accidentally becomes the most popular kid in school", "A mix-up at the post office delivers something hilariously wrong", "The family pet learns to talk — and won't stop complaining", "Your hero signs up for the wrong school club by mistake"],
    2: ["A prank goes completely out of control", "The hero is mistaken for a famous person and can't correct it", "An embarrassing video goes viral overnight", "A dare leads the hero into the most ridiculous situation"],
    3: ["Every attempt to fix the problem makes it twice as funny", "The hero's lie grows bigger and more complicated each day", "A rival keeps accidentally helping instead of hindering", "The hero's disguise fools everyone except the one person who matters"],
    4: ["The big moment arrives and absolutely everything goes wrong at once", "The hero must come clean in the most public way possible", "Two running jokes collide into one spectacular disaster", "The funniest thing that could happen... happens"],
    5: ["Everyone laughs together and forgives the chaos", "The hero learns it's okay to be silly and imperfect", "A friendship is made stronger because of the shared disaster", "Life goes back to normal — until the next ridiculous adventure"],
  },
  Scary: {
    1: ["Your hero starts noticing things that don't quite make sense", "Something is wrong with the new house — but only you can feel it", "A friend dares you to spend the night somewhere nobody goes", "The town has a rule nobody talks about — until now"],
    2: ["A strange noise leads somewhere it shouldn't", "Something that was there yesterday has completely disappeared", "A warning arrives too late to ignore", "The new neighbour knows things they couldn't possibly know"],
    3: ["Every attempt to tell an adult fails in an unexpected way", "The hero realizes they may have made a terrible mistake", "Something is following — but only when they're alone", "The clues point to an answer that can't be real"],
    4: ["The hero must face the source of the fear directly", "They have only one chance to make it right before it's too late", "The truth is revealed and it changes everything", "They must choose between running and staying"],
    5: ["The danger passes — but something is permanently different", "The hero is safe, but not quite sure what they saw", "Life returns to normal — almost", "They know something now that they can never unknow"],
  },
  'Historical Fiction': {
    1: ["Your hero lives in a time very different from today", "Describe a normal day in your hero's world — then something interrupts it", "Your hero has a skill or job unusual for someone their age", "The year, the place, and the problem facing everyone around your hero"],
    2: ["A historical event changes everything for your hero's family", "Your hero is asked to do something that feels wrong but is expected of them", "A stranger arrives from far away with news", "Your hero discovers a truth that the adults are hiding"],
    3: ["The rules of the time work against your hero", "Someone with more power blocks what your hero needs to do", "A choice must be made between safety and doing what's right", "Your hero's plan depends on something outside their control"],
    4: ["The moment that will define who your hero becomes", "A sacrifice must be made — what is your hero willing to give up?", "History is happening around them — how do they find their place in it?", "The hardest conversation your hero has ever had"],
    5: ["How has your hero changed because of what they lived through?", "What does the future look like for them now?", "What did they learn about courage, loyalty, or justice?", "How does their story connect to the world we live in today?"],
  },
  Realistic: {
    1: ["Your hero's ordinary life — school, home, friends, and one thing that quietly bothers them", "A normal Tuesday that is about to become anything but normal", "Your hero has a goal that feels just out of reach", "Describe the place your hero feels most themselves"],
    2: ["Something small happens that turns the day upside down", "A friendship is tested by an unexpected situation", "Your hero is asked to do something they're not sure they can do", "A secret comes out at the worst possible moment"],
    3: ["The harder your hero tries, the more complicated things get", "Someone misunderstands your hero's intentions completely", "A mistake is made that can't easily be taken back", "Your hero has to choose between two people they care about"],
    4: ["The conversation your hero has been avoiding can't wait any longer", "Everything comes to a head at the worst possible time and place", "Your hero has to decide who they really are", "The moment of truth — say it, do it, or walk away?"],
    5: ["What did your hero learn about themselves?", "How is the friendship, family, or situation different now?", "What would your hero do differently — and what are they glad they did?", "Life goes on — but your hero is not quite the same person"],
  },
};
 
interface EnhancedStoryPlannerProps {
  onClose: () => void;
  onJumpToChapter?: (chapterIndex: number) => void;
  isMobile?: boolean;
  onSave?: (planData: { [key: number]: string }) => void;
  storyId?: string;
  initialPlanData?: { [key: number]: string };
}
 
// ── Completion helpers (unchanged) ────────────────────────────────────────────
type PlanData = { [key: number]: string };
 
const isFilled = (text: string | undefined): boolean => {
  if (!text) return false;
  return text.trim().length > 12;
};
 
const countCompletedParts = (planData: PlanData): number => {
  if (!planData) return 0;
  let c = 0;
  if (isFilled(planData[1])) c++;
  if (isFilled(planData[2])) c++;
  if (isFilled(planData[3])) c++;
  if (isFilled(planData[4])) c++;
  if (isFilled(planData[5])) c++;
  return c;
};
 
const getPlannerMessage = (count: number): string => {
  if (count === 0) return "Start your story adventure! ✏️";
  if (count === 1) return "Nice! Your story is beginning!";
  if (count === 2) return "Your world is coming to life 🌎";
  if (count === 3) return "Uh oh… a problem appears!";
  if (count === 4) return "Just the ending left!";
  return "Your story plan is complete! 🎉";
};
 
const EnhancedStoryPlanner: React.FC<EnhancedStoryPlannerProps> = ({
  onClose,
  onJumpToChapter,
  isMobile = false,
  onSave,
  storyId,
  initialPlanData
}) => {
  const { currentUser } = useAuth();
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
 
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
 
  const isMobileView = windowWidth < 768;
  const isTablet     = windowWidth >= 768 && windowWidth < 1100;
 
  interface StickyNote { id: string; content: string; x: number; y: number; }
 
  const [showingIdeas, setShowingIdeas] = useState<{ [key: number]: string[] }>({});
 
  // ── Option C: load saved genre from initialPlanData ───────────────────────
  const [selectedGenre, setSelectedGenre] = useState<string | null>(
    (initialPlanData as any)?.__genre || null
  );
 
  const [planData, setPlanData] = useState<{ [key: number]: string }>(
    initialPlanData
      ? Object.keys(initialPlanData)
          .filter(k => k !== '__stickyNotes' && k !== '__genre')
          .reduce((acc, k) => ({ ...acc, [k]: initialPlanData[parseInt(k)] }), {})
      : {}
  );
 
  const [stickyNotes, setStickyNotes] = useState<{ [key: number]: StickyNote[] }>(() => {
    if (initialPlanData && '__stickyNotes' in initialPlanData) {
      try { return JSON.parse((initialPlanData as any).__stickyNotes); }
      catch { return {}; }
    }
    return {};
  });
 
  const completedParts = countCompletedParts(planData);
  const totalParts = 5;
  const [draggingNote, setDraggingNote] = useState<{ sectionId: number; noteId: string } | null>(null);
 
  // ── Option A: get sparks for a section, preferring genre-specific ones ─────
  const getIdeaSparks = (sectionId: number): string[] => {
    if (selectedGenre && genreIdeaSparks[selectedGenre]?.[sectionId]) {
      return genreIdeaSparks[selectedGenre][sectionId];
    }
    return defaultIdeaSparks[sectionId] || [];
  };
 
  // ── Static data (unchanged) ───────────────────────────────────────────────
  const plannerPrompts: { [key: number]: string } = {
    1: `Who is your main character?\nWhat is their name and age?\nWhat is their normal life like?\nWhere does the story take place?`,
    2: `What unexpected thing happens?\nWho tells them about it?\nWhy can't they ignore it?`,
    3: `What problems do they run into?\nWho or what is stopping them?\nDo they make a mistake?`,
    4: `The BIG moment!\nWhat is the hardest challenge?\nWhat choice must they make?\nAre they brave?`,
    5: `How does everything end?\nWhat did they learn?\nAre they different now?`
  };
 
  // Default (no genre) sparks — kept as before
  const defaultIdeaSparks: { [key: number]: string[] } = {
    1: ["Your hero discovers a hidden talent","Something strange happens in an ordinary place","A mysterious stranger arrives in town","Your hero finds a magical object","An animal becomes their unlikely friend"],
    2: ["A letter arrives with urgent news","Someone needs the hero's help desperately","A competition or challenge is announced","The hero overhears a dangerous secret","Something precious is stolen"],
    3: ["The hero's friend turns against them","They get lost in a dangerous place","Their powers stop working","Someone they trust lies to them","They must solve a tricky puzzle"],
    4: ["The hero must choose between two hard options","Someone betrays them at the worst moment","Their plan fails completely","They face their biggest fear","Time is running out fast"],
    5: ["The hero uses what they learned","An unexpected helper appears","They discover they were brave all along","Everyone comes together to help","They realize what really matters"]
  };
 
  const plotPoints = [
    { id: 1, title: 'The Beginning',         icon: '🌅', description: 'Introduce your hero and their world' },
    { id: 2, title: 'The Call to Adventure',  icon: '📣', description: 'Something exciting happens!' },
    { id: 3, title: 'Rising Action',           icon: '📈', description: 'Challenges and obstacles appear' },
    { id: 4, title: 'The Climax',              icon: '⚡', description: 'The most exciting part!' },
    { id: 5, title: 'Resolution',              icon: '🎉', description: 'How does it all end?' },
  ];
 
  // ── Note handlers (unchanged) ─────────────────────────────────────────────
  const addStickyNote = (sectionId: number) => {
    const n: StickyNote = { id: `note-${Date.now()}`, content: '', x: 40, y: 40 };
    setStickyNotes(p => ({ ...p, [sectionId]: [...(p[sectionId] || []), n] }));
  };
 
  const updateStickyNote = (sectionId: number, noteId: string, updates: Partial<StickyNote>) => {
    setStickyNotes(p => ({
      ...p,
      [sectionId]: (p[sectionId] || []).map(n => n.id === noteId ? { ...n, ...updates } : n)
    }));
  };
 
  const deleteStickyNote = (sectionId: number, noteId: string) => {
    setStickyNotes(p => ({ ...p, [sectionId]: (p[sectionId] || []).filter(n => n.id !== noteId) }));
  };
 
  const handleNoteDrag = (sectionId: number, noteId: string, e: React.MouseEvent<HTMLDivElement>) => {
    if (isMobileView) return;
    e.stopPropagation();
    const note = stickyNotes[sectionId]?.find(n => n.id === noteId);
    if (!note) return;
    const sectionEl = (e.currentTarget as HTMLElement).closest('.planner-section') as HTMLElement;
    if (!sectionEl) return;
    const rect = sectionEl.getBoundingClientRect();
    const offsetX = e.clientX - rect.left - note.x;
    const offsetY = e.clientY - rect.top - note.y;
    const onMove = (mv: MouseEvent) => {
      setStickyNotes(p => ({
        ...p,
        [sectionId]: (p[sectionId] || []).map(n =>
          n.id === noteId ? { ...n, x: mv.clientX - rect.left - offsetX, y: mv.clientY - rect.top - offsetY } : n
        )
      }));
    };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
 
  const hasContent = () =>
    Object.values(planData).some(t => t.trim().length > 0) ||
    Object.values(stickyNotes).some(s => s.some(n => n.content.trim().length > 0));
 
  // ── Option C: include __genre when saving ────────────────────────────────
  const buildSavePayload = () => ({
    ...planData,
    __stickyNotes: JSON.stringify(stickyNotes),
    ...(selectedGenre ? { __genre: selectedGenre } : {}),
  } as any);
 
  useEffect(() => {
    if (!onSave) return;
    const t = setTimeout(() => { onSave(buildSavePayload()); }, 900);
    return () => clearTimeout(t);
  }, [planData, stickyNotes, selectedGenre, onSave]);
 
  useEffect(() => {
    const fn = (e: BeforeUnloadEvent) => { if (!hasContent()) return; e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', fn);
    return () => window.removeEventListener('beforeunload', fn);
  }, [planData, stickyNotes]);
 
  const handleSaveAndClose = () => {
    if (onSave) onSave(buildSavePayload());
    onClose();
  };
 
  // ── Genre sticker toggle (Options A + C) ─────────────────────────────────
  const handleGenreSelect = (label: string) => {
    const next = label === selectedGenre ? null : label;
    setSelectedGenre(next);
    // Clear any open idea panels so they re-open with new genre sparks
    setShowingIdeas({});
  };
 
  // ── Shared sub-components ─────────────────────────────────────────────────
  const SectionCard = ({ point, fullWidth = false }: { point: typeof plotPoints[0]; fullWidth?: boolean }) => {
    const a = ACCENTS[point.id - 1];
    const done = isFilled(planData[point.id]);
 
    return (
      <section style={{
        background: C.surfaceLowest,
        borderRadius: '36px',
        border: `1px solid ${C.outlineVariant}20`,
        padding: '32px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        overflow: 'hidden',
        transition: 'box-shadow 0.3s, border-color 0.3s',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '14px',
          padding: isMobileView ? '10px 20px' : '12px 24px',
          borderBottom: 'none',
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: a.iconBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.5rem', flexShrink: 0,
          }}>
            {point.icon}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: isMobileView ? '1rem' : '1.2rem', color: C.onBackground, margin: 0 }}>
                {point.title}
              </h3>
              {done && (
                <span style={{ background: a.accent, color: '#fff', padding: '2px 9px', borderRadius: '9999px', fontSize: '1.1rem', fontWeight: '700', fontFamily: "'Epilogue', sans-serif" }}>
                  ✓ Done
                </span>
              )}
            </div>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.82rem', color: C.onSurfaceVariant }}>{point.description}</p>
          </div>
        </div>
 
        <div style={{ padding: isMobileView ? '16px 20px' : '20px 28px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
 
          {/* Dot-grid think-about panel */}
          <div style={{
            borderRadius: '24px',
            padding: isMobileView ? '14px 16px' : '18px 20px',
            position: 'relative', overflow: 'hidden',
            background: 'rgba(234, 241, 255, 0.6)', 
            backgroundImage: 'radial-gradient(#dce9fd 1px, transparent 1px)',
            backgroundSize: '20px 20px',
          }}>
            <div style={{ fontSize: '0.85rem', fontWeight: '800', color: a.accent, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', fontFamily: "'Epilogue', sans-serif" }}>
              💡 Think about:
            </div>
            <div style={{ fontSize: isMobileView ? '0.95rem' : '1rem', color: C.onSurfaceVariant, lineHeight: '1.7', whiteSpace: 'pre-line', fontWeight: '500' }}>
              {plannerPrompts[point.id]}
            </div>
            <div style={{ position: 'absolute', bottom: 8, right: 12, fontSize: '3rem', opacity: 0.07, pointerEvents: 'none' }}>
              {point.icon}
            </div>
          </div>
 
          {/* Idea spark button — uses genre sparks if selected (Option A) */}
          <button
            onClick={() => {
              if (showingIdeas[point.id]) {
                const n = { ...showingIdeas }; delete n[point.id]; setShowingIdeas(n);
              } else {
                const sparks = getIdeaSparks(point.id);
                const shuffled = [...sparks].sort(() => Math.random() - 0.5);
                setShowingIdeas({ ...showingIdeas, [point.id]: shuffled.slice(0, 3) });
              }
            }}
            style={{
              width: '100%',
              background: showingIdeas[point.id] ? C.surfaceHigh : 'linear-gradient(135deg, #FFD93D 0%, #FFA500 100%)',
              color: showingIdeas[point.id] ? C.onSurfaceVariant : C.onBackground,
              border: 'none', borderRadius: '12px',
              padding: '11px 16px', fontSize: '0.875rem',
              fontWeight: '700', fontFamily: "'Epilogue', sans-serif",
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              boxShadow: showingIdeas[point.id] ? 'none' : '0 4px 14px rgba(255,165,0,0.3)',
              transition: 'all 0.2s',
            }}
          >
            {showingIdeas[point.id]
              ? '✕ Hide ideas'
              : selectedGenre
                ? `💡 ${selectedGenre} idea!`
                : '💡 Give me an idea!'}
          </button>
 
          {/* Idea list */}
          {showingIdeas[point.id] && (
            <div style={{ background: '#f8f9ff', border: `2px solid ${C.tertiaryContainer}`, borderRadius: '18px', padding: '14px 16px' }}>
              <div style={{ fontSize: '0.9rem', fontWeight: '800', color: C.tertiary, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', fontFamily: "'Epilogue', sans-serif" }}>
                {selectedGenre ? `✨ ${selectedGenre} ideas:` : '✨ Try one of these:'}
              </div>
              {showingIdeas[point.id].map((idea: string, idx: number) => (
                <div key={idx}
                  style={{ padding: '10px 14px', background: C.surfaceLowest, borderRadius: '14px', marginBottom: idx < 2 ? '8px' : 0, fontSize: '1rem', color: C.onSurface, fontWeight: '500', border: `1px solid ${C.tertiaryContainer}`, cursor: 'pointer', transition: 'background 0.15s' }}
                  onClick={() => {
                    const n: StickyNote = { id: `note-${Date.now()}-${idx}`, content: idea, x: 40 + idx * 20, y: 40 + idx * 20 };
                    setStickyNotes(p => ({ ...p, [point.id]: [...(p[point.id] || []), n] }));
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#FFFBF0'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = C.surfaceLowest; }}
                >
                  → {idea}
                </div>
              ))}
            </div>
          )}
 
          {/* Brainstorm zone + sticky notes */}
          <div style={{ background: C.surface, border: `2px dashed ${C.outlineVariant}80`, borderRadius: '18px', padding: isMobileView ? '12px' : '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: '800', color: C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: "'Epilogue', sans-serif" }}>
                📌 Brainstorm Zone
              </div>
              <button onClick={() => addStickyNote(point.id)}
                style={{ background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, color: '#fff', border: 'none', borderRadius: '9999px', padding: '6px 14px', fontSize: '0.78rem', fontWeight: '700', fontFamily: "'Epilogue', sans-serif", cursor: 'pointer', boxShadow: `0 3px 10px ${C.primary}30` }}>
                + Add Note
              </button>
            </div>
 
            {isMobileView ? (
              <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '6px', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
                {(stickyNotes[point.id] || []).length === 0 ? (
                  <div style={{ color: C.outlineVariant, fontSize: '0.82rem', fontWeight: '600', padding: '16px 0', textAlign: 'center', width: '100%' }}>
                    Tap "+ Add Note" to brainstorm!
                  </div>
                ) : (stickyNotes[point.id] || []).map(note => (
                  <MobileStickyNote key={note.id} note={note} sectionId={point.id}
                    onUpdate={updateStickyNote} onDelete={deleteStickyNote} />
                ))}
              </div>
            ) : (
              <div className="planner-section" style={{ position: 'relative', minHeight: '200px' }}>
                {(stickyNotes[point.id] || []).length === 0 && (
                  <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center', color: C.outlineVariant, fontSize: '0.82rem', fontWeight: '600', width: '100%', pointerEvents: 'none' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📝</div>
                    Click "+ Add Note" to start brainstorming!
                  </div>
                )}
                {(stickyNotes[point.id] || []).map(note => (
                  <DesktopStickyNote key={note.id} note={note} sectionId={point.id}
                    isDragging={draggingNote?.noteId === note.id}
                    onMouseDown={handleNoteDrag}
                    onUpdate={updateStickyNote} onDelete={deleteStickyNote} />
                ))}
              </div>
            )}
          </div>
 
          {/* Summary textarea */}
          <div>
            <div style={{ fontSize: '0.82rem', fontWeight: '800', color: C.onSurfaceVariant, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: "'Epilogue', sans-serif" }}>
              ✍️ Summary <span style={{ fontWeight: '500', textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
            </div>
            <textarea
              value={planData[point.id] || ''}
              onChange={e => setPlanData({ ...planData, [point.id]: e.target.value })}
              placeholder="Organize your ideas into a short summary..."
              style={{ width: '100%', minHeight: '80px', padding: '12px 14px', border: `1px solid ${C.outlineVariant}80`, borderRadius: '14px', fontSize: '1rem', fontFamily: "'Plus Jakarta Sans', sans-serif", resize: 'vertical', outline: 'none', boxSizing: 'border-box' as const, lineHeight: '1.55', background: C.surfaceLowest, color: C.onSurface, transition: 'border-color 0.15s' }}
              onFocus={e => { e.target.style.borderColor = a.accent; }}
              onBlur={e => { e.target.style.borderColor = C.outlineVariant; }}
            />
          </div>
        </div>
      </section>
    );
  };
 
  // ── Render ────────────────────────────────────────────────────────────────
  const useDesktopLayout = !isMobileView && !isTablet;
 
  return (
    <>
      <div style={{ background: C.surface, minHeight: '100vh', paddingBottom: '60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
 
        {/* ── Top bar ───────────────────────────────────────────────────────── */}
        <div style={{
          height: isMobileView ? 52 : 60,
          background: C.surfaceLowest,
          borderBottom: `1px solid ${C.surfaceHigh}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: isMobileView ? '0 16px' : '0 32px',
          position: 'sticky', top: 0, zIndex: 50,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '1.3rem' }}>📝</span>
            <span style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: isMobileView ? '1rem' : '1.1rem', color: C.onBackground }}>Story Planner</span>
            {selectedGenre && (
              <span style={{ background: GENRE_STICKERS.find(g => g.label === selectedGenre)?.bg, color: GENRE_STICKERS.find(g => g.label === selectedGenre)?.color, padding: '2px 10px', borderRadius: '9999px', fontSize: '0.65rem', fontWeight: '800', fontFamily: "'Epilogue', sans-serif" }}>
                {selectedGenre}
              </span>
            )}
          </div>
          <button onClick={handleSaveAndClose} style={{ background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, color: '#fff', border: 'none', borderRadius: '9999px', padding: isMobileView ? '7px 16px' : '9px 22px', fontSize: isMobileView ? '0.78rem' : '0.85rem', fontWeight: '700', fontFamily: "'Epilogue', sans-serif", cursor: 'pointer', boxShadow: `0 4px 12px ${C.primary}40`, whiteSpace: 'nowrap' }}>
            {isMobileView ? 'Done ✓' : 'Return to Writing →'}
          </button>
        </div>
 
        {/* ── Page content ───────────────────────────────────────────────── */}
        <div style={{ maxWidth: '1300px', margin: '0 auto', padding: isMobileView ? '20px 16px' : '32px 40px' }}>
 
          {/* ── Hero header ─────────────────────────────────────────────── */}
          <div style={{ marginBottom: isMobileView ? '24px' : '40px', position: 'relative' }}>
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: isMobileView ? '2.4rem' : '4rem', color: C.primary, lineHeight: 1.05, letterSpacing: '-2px', margin: '0 0 8px 0' }}>
                Plotting Your<br />
                <span style={{ color: C.onBackground }}>
                  {selectedGenre ? <><em style={{ fontStyle: 'italic', color: C.onBackground }}>{selectedGenre}</em>{' '}</> : ''}Adventure
                </span>
              </h2>
              <p style={{ fontSize: isMobileView ? '1.0rem' : '1.10rem', color: C.onSurfaceVariant, maxWidth: '500px', marginBottom: isMobileView ? '16px' : '24px', lineHeight: 1.6 }}>
                {selectedGenre
                  ? `Great choice! Your ideas will be tailored to ${selectedGenre} stories.`
                  : 'Every great story starts with a single spark. Plant your seeds and watch them grow.'}
              </p>
 
              {/* Progress + Give Me An Idea button row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    const incomplete = plotPoints.filter(p => !isFilled(planData[p.id]));
                    const target = incomplete.length > 0 ? incomplete[Math.floor(Math.random() * incomplete.length)] : plotPoints[0];
                    const sparks = getIdeaSparks(target.id);
                    const shuffled = [...sparks].sort(() => Math.random() - 0.5);
                    setShowingIdeas(prev => ({ ...prev, [target.id]: shuffled.slice(0, 3) }));
                    document.getElementById(`section-${target.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  style={{ background: 'linear-gradient(135deg, #FFD93D, #FFA500)', color: C.onBackground, border: 'none', borderRadius: '9999px', padding: isMobileView ? '12px 24px' : '14px 32px', fontSize: isMobileView ? '0.9rem' : '1rem', fontWeight: '700', fontFamily: "'Epilogue', sans-serif", cursor: 'pointer', boxShadow: '0 4px 16px rgba(255,165,0,0.35)', display: 'flex', alignItems: 'center', gap: '8px', transition: 'transform 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  {selectedGenre ? `✨ ${selectedGenre} Idea!` : '✨ Give Me An Idea!'}
                </button>
 
                {/* Progress pill */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: C.surfaceLowest, borderRadius: '9999px', padding: '8px 16px', border: 'none' }}>
                  <div style={{ width: 80, height: 6, background: C.surfaceHigh, borderRadius: '9999px', overflow: 'hidden' }}>
                    <div style={{ width: `${(completedParts / totalParts) * 100}%`, height: '100%', background: completedParts === totalParts ? C.secondary : C.primary, borderRadius: '9999px', transition: 'width 0.4s' }} />
                  </div>
                  <span style={{ fontSize: '0.8rem', fontWeight: '700', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif" }}>
                    {completedParts}/{totalParts} {getPlannerMessage(completedParts).replace(/!.*/, '')}
                  </span>
                </div>
              </div>
            </div>
          </div>
 
          {/* ── Two-column editorial layout (desktop) / single column (mobile/tablet) ── */}
          {useDesktopLayout ? (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '28px', alignItems: 'start', marginBottom: '28px' }}>
 
                {/* LEFT: sections 1–3 */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {plotPoints.slice(0, 3).map(p => (
                    <div id={`section-${p.id}`} key={p.id}>
                      <SectionCard point={p} />
                    </div>
                  ))}
                </div>
 
                {/* RIGHT: sticky sidebar */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', position: 'sticky', top: '80px' }}>
 
                  {/* Progress card */}
                  <div style={{ background: '#ffffff', borderRadius: '28px', padding: '20px 24px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
                    <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.5rem', color: C.onBackground, marginBottom: '12px' }}>
                      📊 Your Progress
                    </div>
                    {plotPoints.map(p => {
                      const done = isFilled(planData[p.id]);
                      return (
                        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                          <div style={{ width: 28, height: 28, borderRadius: '50%', background: done ? C.primary : C.surfaceHigh, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: '800', color: done ? '#fff' : C.outlineVariant, flexShrink: 0, transition: 'background 0.3s' }}>
                            {done ? '✓' : p.id}
                          </div>
                          <span style={{ fontSize: '1rem', fontWeight: done ? '700' : '500', color: done ? C.onBackground : C.onSurfaceVariant }}>{p.title}</span>
                          {done && <span style={{ marginLeft: 'auto', background: C.secondaryContainer, color: C.secondary, padding: '1px 8px', borderRadius: '9999px', fontSize: '0.6rem', fontWeight: '800' }}>Done</span>}
                        </div>
                      );
                    })}
                    <div style={{ height: 6, background: C.surfaceHigh, borderRadius: '9999px', overflow: 'hidden', marginTop: '12px' }}>
                      <div style={{ width: `${(completedParts / totalParts) * 100}%`, height: '100%', background: completedParts === totalParts ? C.secondary : `linear-gradient(90deg, ${C.primary}, ${C.primaryLight})`, borderRadius: '9999px', transition: 'width 0.5s' }} />
                    </div>
                    <div style={{ fontSize: '0.75rem', color: C.onSurfaceVariant, marginTop: '6px', fontWeight: '600' }}>
                      {getPlannerMessage(completedParts)}
                    </div>
                  </div>
 
                  {/* Writing tips card */}
                  <div style={{ background: `linear-gradient(135deg, ${C.primary} 0%, #5e26c7 100%)`, borderRadius: '24px', padding: '20px 24px', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.08)', borderRadius: '50%' }} />
                    <div style={{ position: 'absolute', bottom: -10, left: -10, width: 60, height: 60, background: 'rgba(255,255,255,0.05)', borderRadius: '50%' }} />
                    <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.78rem', color: C.primaryLight, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                      ✏️ Writing Tip
                    </div>
                    <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.9)', lineHeight: 1.6, margin: '0 0 10px 0', fontWeight: '500', position: 'relative', zIndex: 1 }}>
                      "Show, don't tell! Instead of saying 'She was scared,' describe how her hands shook and her voice went quiet."
                    </p>
                    <div style={{ fontSize: '0.72rem', color: C.primaryLight, fontWeight: '700', position: 'relative', zIndex: 1 }}>— Sparky the Muse ✨</div>
                  </div>
 
                  {/* Genre stickers — now functional (Options A + C) */}
                  <div style={{ background: C.surfaceHigh, borderRadius: '24px', padding: '20px 24px' }}>
                    <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.95rem', color: C.onBackground, marginBottom: '4px' }}>
                      🎭 Genre Stickers
                    </div>
                    <div style={{ fontSize: '0.78rem', color: C.onSurfaceVariant, marginBottom: '12px' }}>
                      Pick your genre — ideas will match!
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {GENRE_STICKERS.map(g => {
                        const isSelected = selectedGenre === g.label;
                        return (
                          <span key={g.label}
                            onClick={() => handleGenreSelect(g.label)}
                            style={{
                              padding: '8px 16px',
                              background: isSelected ? g.color : g.bg,
                              color: isSelected ? '#fff' : g.color,
                              borderRadius: '9999px',
                              fontSize: '0.78rem',
                              fontWeight: '800',
                              fontFamily: "'Epilogue', sans-serif",
                              cursor: 'pointer',
                              transition: 'transform 0.15s, background 0.2s, box-shadow 0.2s',
                              display: 'inline-block',
                              boxShadow: isSelected ? `0 4px 14px ${g.color}50` : 'none',
                              outline: isSelected ? `2px solid ${g.color}` : 'none',
                              outlineOffset: '2px',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'rotate(-3deg) scale(1.1)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'rotate(0deg) scale(1)'; }}
                          >
                            {g.label.toUpperCase()}
                          </span>
                        );
                      })}
                    </div>
                    {selectedGenre && (
                      <div style={{ marginTop: '12px', fontSize: '0.75rem', color: C.onSurfaceVariant }}>
                        ✓ Ideas are now tailored to <strong>{selectedGenre}</strong>. Tap again to deselect.
                      </div>
                    )}
                  </div>
                </div>
              </div>
 
              {/* FULL WIDTH: sections 4 (Climax) and 5 (Resolution) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div id="section-4">
                  <section style={{
                    background: C.surfaceHigh,
                    borderRadius: '48px',
                    border: '1px solid rgba(255,255,255,0.5)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
                    position: 'relative',
                  }}>
                    <div style={{ position: 'absolute', top: 0, right: 0, width: 200, height: 200, background: C.surfaceSoft, borderRadius: '50%', filter: 'blur(60px)', opacity: 0.4, transform: 'translate(40%, -40%)', pointerEvents: 'none' }} />
                    <div style={{ position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: '1fr 300px', gap: '0', alignItems: 'stretch' }}>
                      <div style={{ padding: '32px 36px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                          <div style={{ width: 56, height: 56, borderRadius: '16px', background: C.errorContainer, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', border: `2px solid ${C.error}30` }}>⚡</div>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '2rem', color: C.onBackground, margin: 0 }}>The Climax</h3>
                              {isFilled(planData[4]) && <span style={{ background: C.error, color: '#fff', padding: '2px 10px', borderRadius: '9999px', fontSize: '0.6rem', fontWeight: '800', fontFamily: "'Epilogue', sans-serif" }}>✓ Done</span>}
                            </div>
                            <p style={{ margin: '2px 0 0 0', fontSize: '0.9rem', color: C.onSurfaceVariant }}>The moment of highest tension! The confrontation. The turning point where everything changes.</p>
                          </div>
                        </div>
 
                        {/* Climax idea spark — also genre-aware */}
                        <button onClick={() => {
                          if (showingIdeas[4]) { const n = { ...showingIdeas }; delete n[4]; setShowingIdeas(n); }
                          else { const sparks = getIdeaSparks(4); const s = [...sparks].sort(() => Math.random() - 0.5); setShowingIdeas({ ...showingIdeas, 4: s.slice(0, 3) }); }
                        }} style={{ background: showingIdeas[4] ? C.surfaceHigh : 'linear-gradient(135deg, #FFD93D, #FFA500)', color: showingIdeas[4] ? C.onSurfaceVariant : C.onBackground, border: 'none', borderRadius: '12px', padding: '10px 18px', fontSize: '0.875rem', fontWeight: '700', fontFamily: "'Epilogue', sans-serif", cursor: 'pointer', marginBottom: '14px', boxShadow: showingIdeas[4] ? 'none' : '0 4px 14px rgba(255,165,0,0.3)', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
                          {showingIdeas[4] ? '✕ Hide ideas' : selectedGenre ? `💡 ${selectedGenre} idea!` : '💡 Give me an idea!'}
                        </button>
                        {showingIdeas[4] && (
                          <div style={{ background: '#f8f9ff', border: `2px solid ${C.tertiaryContainer}`, borderRadius: '14px', padding: '14px 16px', marginBottom: '14px' }}>
                            {showingIdeas[4].map((idea: string, idx: number) => (
                              <div key={idx} style={{ padding: '9px 12px', background: C.surfaceLowest, borderRadius: '10px', marginBottom: idx < 2 ? '6px' : 0, fontSize: '1rem', color: C.onSurface, border: `1px solid ${C.tertiaryContainer}80`, cursor: 'pointer' }}
                                onClick={() => { const n: StickyNote = { id: `note-${Date.now()}-${idx}`, content: idea, x: 40 + idx * 20, y: 40 + idx * 20 }; setStickyNotes(p => ({ ...p, 4: [...(p[4] || []), n] })); }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#FFFBF0'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = C.surfaceLowest; }}>
                                → {idea}
                              </div>
                            ))}
                          </div>
                        )}
 
                        <div style={{ borderRadius: '20px', padding: '20px', background: 'rgba(255,255,255,0.6)', backgroundImage: 'radial-gradient(#00000008 1px, transparent 1px)', backgroundSize: '20px 20px', border: `1px solid ${C.outlineVariant}30`, boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.04)', marginBottom: '16px' }}>
                          <div style={{ fontSize: '0.7rem', fontWeight: '800', color: C.error, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', fontFamily: "'Epilogue', sans-serif" }}>⚡ Brainstorm Zone</div>
                          <div style={{ fontSize: '0.82rem', color: C.onSurfaceVariant, lineHeight: '1.65', whiteSpace: 'pre-line', marginBottom: '12px', fontWeight: '500' }}>{plannerPrompts[4]}</div>
                          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                            <button onClick={() => addStickyNote(4)} style={{ background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, color: '#fff', border: 'none', borderRadius: '9999px', padding: '6px 14px', fontSize: '0.75rem', fontWeight: '700', fontFamily: "'Epilogue', sans-serif", cursor: 'pointer' }}>+ Add Note</button>
                          </div>
                          <div className="planner-section" style={{ position: 'relative', minHeight: (stickyNotes[4] || []).length ? '180px' : '40px' }}>
                            {(stickyNotes[4] || []).length === 0 && <div style={{ fontSize: '0.78rem', color: C.outlineVariant, fontStyle: 'italic' }}>Add sticky notes to brainstorm your climax…</div>}
                            {(stickyNotes[4] || []).map(note => (
                              <DesktopStickyNote key={note.id} note={note} sectionId={4} isDragging={draggingNote?.noteId === note.id} onMouseDown={handleNoteDrag} onUpdate={updateStickyNote} onDelete={deleteStickyNote} />
                            ))}
                          </div>
                        </div>
 
                        <div>
                          <div style={{ fontSize: '0.72rem', fontWeight: '800', color: C.onSurfaceVariant, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: "'Epilogue', sans-serif" }}>✍️ Summary</div>
                          <textarea value={planData[4] || ''} onChange={e => setPlanData({ ...planData, 4: e.target.value })} placeholder="Write your epic showdown..." style={{ width: '100%', minHeight: '90px', padding: '12px 14px', border: `1.5px solid ${C.outlineVariant}`, borderRadius: '10px', fontSize: '1rem', fontFamily: "'Plus Jakarta Sans', sans-serif", resize: 'vertical', outline: 'none', boxSizing: 'border-box' as const, lineHeight: '1.55', background: C.surfaceLowest, color: C.onSurface, transition: 'border-color 0.15s' }}
                            onFocus={e => { e.target.style.borderColor = C.error; }}
                            onBlur={e => { e.target.style.borderColor = C.outlineVariant; }} />
                        </div>
                      </div>
                      <div style={{ background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8rem' }}>
                        ⚡
                      </div>
                    </div>
                  </section>
                </div>
 
                <div id="section-5">
                  <SectionCard point={plotPoints[4]} fullWidth />
                </div>
              </div>
            </div>
          ) : (
            /* ── MOBILE / TABLET: single column ─────────────────────────── */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Genre stickers on mobile — shown above sections */}
              <div style={{ background: C.surfaceLowest, borderRadius: '20px', padding: '16px 20px', border: `1px solid ${C.surfaceHigh}` }}>
                <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.9rem', color: C.onBackground, marginBottom: '4px' }}>🎭 Pick your genre</div>
                <div style={{ fontSize: '0.75rem', color: C.onSurfaceVariant, marginBottom: '10px' }}>Ideas will match your genre!</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {GENRE_STICKERS.map(g => {
                    const isSelected = selectedGenre === g.label;
                    return (
                      <span key={g.label}
                        onClick={() => handleGenreSelect(g.label)}
                        style={{ padding: '7px 14px', background: isSelected ? g.color : g.bg, color: isSelected ? '#fff' : g.color, borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '800', fontFamily: "'Epilogue', sans-serif", cursor: 'pointer', display: 'inline-block', boxShadow: isSelected ? `0 4px 12px ${g.color}50` : 'none', transition: 'all 0.2s' }}>
                        {g.label.toUpperCase()}
                      </span>
                    );
                  })}
                </div>
              </div>
              {plotPoints.map(p => (
                <div id={`section-${p.id}`} key={p.id}>
                  <SectionCard point={p} />
                </div>
              ))}
            </div>
          )}
 
        </div>
      </div>
 
      <SignupPrompt isOpen={showSignupPrompt} onClose={() => setShowSignupPrompt(false)} reason="save" />
    </>
  );
};
 
// ── Desktop sticky note ───────────────────────────────────────────────────────
const DesktopStickyNote: React.FC<{
  note: { id: string; content: string; x: number; y: number };
  sectionId: number; isDragging: boolean;
  onMouseDown: (sectionId: number, noteId: string, e: React.MouseEvent<HTMLDivElement>) => void;
  onUpdate: (sectionId: number, noteId: string, updates: any) => void;
  onDelete: (sectionId: number, noteId: string) => void;
}> = ({ note, sectionId, isDragging, onMouseDown, onUpdate, onDelete }) => (
  <div
    onMouseDown={e => { if ((e.target as HTMLElement).tagName === 'TEXTAREA') return; onMouseDown(sectionId, note.id, e); }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'rotate(0deg) scale(1.04)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'rotate(-1.5deg)'; }}
    style={{ position: 'absolute', left: `${note.x}px`, top: `${note.y}px`, width: '180px', background: '#FFF9C4', border: '2px solid #FDD835', borderRadius: '14px', padding: '28px 12px 12px 12px', boxShadow: '5px 5px 15px rgba(0,0,0,0.1)', cursor: 'grab', zIndex: isDragging ? 100 : 1, transform: 'rotate(-1deg)', transition: 'transform 0.15s ease' }}
  >
    <button onClick={() => onDelete(sectionId, note.id)} style={{ position: 'absolute', top: 7, right: 7, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1rem', padding: '2px', lineHeight: 1, color: '#a4aebe' }}
      onMouseEnter={e => { e.currentTarget.style.color = '#b41340'; }}
      onMouseLeave={e => { e.currentTarget.style.color = '#a4aebe'; }}>🗑️</button>
    <textarea value={note.content} onChange={e => onUpdate(sectionId, note.id, { content: e.target.value })} onClick={e => e.stopPropagation()} placeholder="Write your idea..." style={{ width: '100%', minHeight: '80px', border: 'none', background: 'transparent', fontSize: '0.875rem', fontFamily: "'Plus Jakarta Sans', sans-serif", resize: 'none', outline: 'none', lineHeight: '1.45', color: '#252f3c' }} />
  </div>
);
 
// ── Mobile sticky note (horizontal card) ──────────────────────────────────────
const MobileStickyNote: React.FC<{
  note: { id: string; content: string; x: number; y: number };
  sectionId: number;
  onUpdate: (sectionId: number, noteId: string, updates: any) => void;
  onDelete: (sectionId: number, noteId: string) => void;
}> = ({ note, sectionId, onUpdate, onDelete }) => (
  <div style={{ flexShrink: 0, width: '160px', background: '#FFF9C4', border: '2px solid #FDD835', borderRadius: '14px', padding: '12px', boxShadow: '0 3px 10px rgba(0,0,0,0.1)' }}>
    <button onClick={() => onDelete(sectionId, note.id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '0.9rem', color: '#a4aebe', float: 'right', padding: 0, lineHeight: 1 }}
      onMouseEnter={e => { e.currentTarget.style.color = '#b41340'; }}
      onMouseLeave={e => { e.currentTarget.style.color = '#a4aebe'; }}>🗑️</button>
    <div style={{ clear: 'both' }} />
    <textarea value={note.content} onChange={e => onUpdate(sectionId, note.id, { content: e.target.value })} placeholder="Your idea..." style={{ width: '100%', minHeight: '60px', border: 'none', background: 'transparent', fontSize: '0.82rem', fontFamily: "'Plus Jakarta Sans', sans-serif", resize: 'none', outline: 'none', lineHeight: '1.4', color: '#252f3c', boxSizing: 'border-box' as const }} />
  </div>
);
 
export default EnhancedStoryPlanner;